/**
 * Onboarding Modal
 *
 * Progressive onboarding flow for new users with context-aware steps.
 * Issue #563 - First-Run Onboarding Flow
 * Issue #692 - Club-First Onboarding (B1 Path)
 * Issue #830 - Redirect URL stored in D1, written to sessionStorage by MagicLoginPage
 */

import { useState, useEffect, useMemo } from 'react';

// Pending destination (#830): written by MagicLoginPage and LoginModal's SMS
// verify (same tab), read here after onboarding.
import { PENDING_NAV_KEY } from '../../lib/pendingNavigation';
import { isSafeRedirectPath } from '../../lib/safeRedirect';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Me, OnboardingContext } from '@/lib/api/auth';
import { authApi } from '@/lib/api/auth';
import WelcomeStep from './steps/WelcomeStep';
import ProfileBasicsStep from './steps/ProfileBasicsStep';
import CreateClubStep from './steps/CreateClubStep';
import ActivitiesStep from './steps/ActivitiesStep';
import UnitsStep from './steps/UnitsStep';
import NotificationsStep from './steps/NotificationsStep';
import CompletionStep from './steps/CompletionStep';

interface OnboardingModalProps {
  user: Me;
  context?: OnboardingContext; // Optional - defaults to 'clean' signup
}

type StepType = 'welcome' | 'profile' | 'createclub' | 'activities' | 'units' | 'notifications' | 'completion';

export default function OnboardingModal({ user, context }: OnboardingModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Default context for users without invite context (direct signups)
  const onboardingContext: OnboardingContext = context || { type: 'clean' };

  // Track club slug (for Path B1 - created during onboarding). #2692: for an
  // invited 'club' context, seed from club_slug (the routable slug), NOT
  // club_id (the UUID) — routing to /clubs/:uuid 404s.
  const [clubSlug, setClubSlug] = useState<string | null>(
    onboardingContext.type === 'club' ? onboardingContext.club_slug || null : null
  );
  // Name of the club created during onboarding, for the CreateClub re-entry
  // confirmation on Back (#2693).
  const [createdClubName, setCreatedClubName] = useState<string | null>(null);

  // Determine which steps to show based on context (Issue #692).
  //
  // #2693: key the CreateClub step ONLY on context.type, never on the live
  // clubSlug. A clean signup always gets the CreateClub step, and the list stays
  // STABLE for the whole flow. The old `&& !clubSlug` condition contracted the
  // array the moment handleClubCreated set the slug — but currentStepIndex had
  // already advanced past 'createclub', so the now-shorter array left the index
  // pointing at 'units', silently skipping 'activities' (where primary_sport is
  // collected). Removing the mutable-slug dependency eliminates the contraction
  // entirely, so there is no stale index to freeze or adjust. clubSlug is still
  // tracked below, but only for post-onboarding navigation (resolveDestination).
  const steps: StepType[] = useMemo(() => {
    const baseSteps: StepType[] = ['welcome', 'profile'];

    // Path B1: a clean signup (no invited/founder club) gets the CreateClub step.
    if (onboardingContext.type === 'clean') {
      baseSteps.push('createclub');
    }

    baseSteps.push('activities', 'units', 'notifications', 'completion');
    return baseSteps;
  }, [onboardingContext.type]);

  const currentStep = steps[currentStepIndex];

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Prevent ESC key from closing modal (non-dismissible)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleClubCreated = (slug: string, name: string) => {
    setClubSlug(slug);
    setCreatedClubName(name);
    // An SMS sign-in from "Set up your next event" mounts /events/create
    // UNDER this modal, so its managed-club list is already cached as empty
    // (5-min staleTime) by the time the club exists. Mark it stale so the
    // page refetches and preselects the new club (#3517; Frank r2 on #3530).
    void queryClient.invalidateQueries({ queryKey: ['clubs-for-event-creation'] });
    // Slug stored for navigation after onboarding completion; name for the
    // CreateClub re-entry confirmation (#2693).
  };

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  // Resolve the intended post-onboarding destination. Extracted so the
  // success path AND the failure-recovery path (#2702) navigate identically —
  // the recovery path must run the SAME context routing, not a degraded
  // clubSlug-or-home fallback that silently drops invite/event/pending-nav
  // destinations. Consumes the pending-nav sessionStorage key on read; safe
  // because exactly one navigation path runs per handleComplete call.
  const resolveDestination = (): string => {
    // Check for stored pending navigation first (Issue #811)
    // This survives the magic link redirect flow
    let destination = '/home'; // Fallback (should never be used)
    try {
      const storedNav = sessionStorage.getItem(PENDING_NAV_KEY);
      if (storedNav) {
        // #3544: only an app-relative path; anything else falls through to
        // the context-based routing below.
        if (isSafeRedirectPath(storedNav)) destination = storedNav;
        sessionStorage.removeItem(PENDING_NAV_KEY); // Clean up
      }
    } catch {
      // sessionStorage unavailable (React Native, privacy mode)
    }

    // If no stored navigation, fall back to context-based routing (Issue #692)
    if (destination === '/home') {
      if (onboardingContext.type === 'club' && onboardingContext.club_slug) {
        // Path A: Invited to club. #2692: route on the slug (web /clubs/:slug),
        // never club_id (UUID) — the old code landed the invited user on a 404.
        destination = `/clubs/${onboardingContext.club_slug}`;
      } else if (onboardingContext.type === 'founder' && onboardingContext.club_id) {
        // Founder path
        destination = `/clubs/${onboardingContext.club_id}`;
      } else if (onboardingContext.type === 'event' && onboardingContext.event_id) {
        // Event invite path
        destination = `/event/${onboardingContext.event_id}`;
      } else if (clubSlug) {
        // Path B1: Created club during onboarding (using slug for routing)
        destination = `/clubs/${clubSlug}`;
      }
    }

    return destination;
  };

  const handleComplete = async () => {
    setIsSubmitting(true);

    try {
      // Mark onboarding as complete
      await authApi.completeOnboarding();

      // Invalidate user cache so App.tsx sees onboarding_completed = true (#735)
      await queryClient.invalidateQueries({ queryKey: ['me'] });

      navigate(resolveDestination());
    } catch (error) {
      // #2702: the completion PATCH can 500 AFTER the flag committed server-side
      // (a post-write side effect failed). In that case the flag IS true; if we
      // just fall back to a degraded destination the intended invite/event/
      // pending-nav navigation is silently lost. Refetch me: if onboarding
      // flipped true, treat as success and run the SAME intended navigation.
      console.error('Failed to complete onboarding:', error);
      try {
        const me = await authApi.me();
        if (me?.onboarding_completed) {
          // Seed the verified completed user DIRECTLY before navigating (#2702
          // r1 — Frank). Do NOT rely on invalidateQueries alone: that fires a
          // SECOND /auth/me, and if THAT request fails the query cache keeps the
          // stale onboarding_completed:false user while navigation proceeds under
          // the still-mounted gate. setQueryData writes the raw Me object at the
          // ['me'] key — the exact shape useMe caches (useMe.ts) and that
          // LoginModal/MagicLoginPage seed — so the gate closes on the value we
          // just confirmed, not on a request that might not land.
          queryClient.setQueryData(['me'], me);
          navigate(resolveDestination());
          // Best-effort background refresh; recovery does not depend on it
          // succeeding (a failed refetch leaves the seeded cache intact).
          void queryClient.invalidateQueries({ queryKey: ['me'] }).catch(() => {});
          return;
        }
      } catch (refetchError) {
        console.error('Onboarding recovery refetch failed:', refetchError);
      }
      // Genuine failure (flag not committed / refetch failed): fail gracefully.
      navigate(clubSlug ? `/clubs/${clubSlug}` : '/home');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-oro-bg flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
      <div
        className="relative w-full max-w-md max-h-[90vh] rounded-2xl bg-oro-surface shadow-2xl overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
      >
        {currentStep === 'welcome' && (
          <WelcomeStep
            user={user}
            context={onboardingContext}
            onNext={handleNext}
          />
        )}

        {currentStep === 'profile' && (
          <ProfileBasicsStep
            user={user}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}

        {currentStep === 'createclub' && (
          <CreateClubStep
            onNext={handleNext}
            onBack={handleBack}
            onClubCreated={handleClubCreated}
            createdClubSlug={clubSlug}
            createdClubName={createdClubName}
          />
        )}

        {currentStep === 'activities' && (
          <ActivitiesStep
            onNext={handleNext}
            onBack={handleBack}
          />
        )}

        {currentStep === 'units' && (
          <UnitsStep
            onNext={handleNext}
            onBack={handleBack}
          />
        )}

        {currentStep === 'notifications' && (
          <NotificationsStep
            onNext={handleNext}
            onBack={handleBack}
          />
        )}

        {currentStep === 'completion' && (
          <CompletionStep
            context={onboardingContext}
            clubContext={clubSlug}
            onComplete={handleComplete}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    </div>
  );
}
